import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, TextField, Button, Chip,
    Divider, IconButton, Tooltip, Alert
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUpload from "../components/FileUpload";
import axios from "axios";
import config from "../config";
import { useAuth } from "../context/AuthContext";

function normalizeFieldValue(fieldVal) {
    if (fieldVal == null) return { value: "", evidenceDate: null, attachment: null };
    if (typeof fieldVal === "string") {
        return { value: fieldVal, evidenceDate: null, attachment: null };
    }
    if (typeof fieldVal === "number") {
        // Treat 0 as empty so placeholders show on edit
        return { value: fieldVal === 0 ? "" : fieldVal, evidenceDate: null, attachment: null };
    }
    const rawValue = fieldVal.value;
    // Treat 0 as empty so placeholders show on edit instead of "0"
    const value = (rawValue === 0) ? "" : (rawValue ?? "");
    return {
        value,
        evidenceDate: fieldVal.evidenceDate ? new Date(fieldVal.evidenceDate) : null,
        attachment: fieldVal.attachment ?? null,
    };
}

function useConferenceState(initialPurchase, confFields) {
    const initial = useMemo(() => {
        const meta = initialPurchase?.metadata || {};
        const obj = {};
        confFields.forEach((f) => {
            obj[f.key] = normalizeFieldValue(meta[f.key]);
        });
        return {
            dateOfPurchase: initialPurchase?.dateOfPurchase || "",
            fields: obj,
        };
    }, [initialPurchase, confFields]);

    const [state, setState] = useState(initial);
    useEffect(() => setState(initial), [initial]);
    return [state, setState];
}

export default function ConferenceModal({
                                            open,
                                            onClose,
                                            onSave,
                                            onDelete,
                                            initialPurchase,
                                            categoryFields,
                                            mode = "create", // "create" | "edit"
                                            disallowFutureEvidenceDates = true,
                                        }) {
    const fieldDefs = useMemo(
        () => (categoryFields || []).filter((f) => f.key !== "dateOfPurchase"),
        [categoryFields]
    );

    const { token } = useAuth();
    const [state, setState] = useConferenceState(initialPurchase, fieldDefs);
    const [error, setError] = useState("");
    const [busy] = useState(false);     // reserved for future async ops
    const [saving, setSaving] = useState(false); // spinner state for save/update
    const [downloadingKey, setDownloadingKey] = useState(null); // spinner state for downloads

    const handleChangeValue = (key, raw) => {
        setState((prev) => ({
            ...prev,
            fields: { ...prev.fields, [key]: { ...prev.fields[key], value: raw } },
        }));
    };

    const handleChangeDate = (key, newDate) => {
        if (disallowFutureEvidenceDates && newDate) {
            const today = new Date();
            if (new Date(newDate) > today) {
                setError("Evidence date cannot be in the future.");
                return;
            }
        }
        setError("");
        setState((prev) => ({
            ...prev,
            fields: { ...prev.fields, [key]: { ...prev.fields[key], evidenceDate: newDate } },
        }));
    };

    const handleDownloadAttachment = async (key) => {
        if (!initialPurchase?._id) return;
        setDownloadingKey(key);
        setError("");
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const encodedId = encodeURIComponent(initialPurchase._id);
            const encodedKey = encodeURIComponent(key);
            const response = await axios.get(
                `${config.SERVER_URL}/api/conference/${encodedId}/attachment/${encodedKey}`,
                { headers }
            );
            const url = response.data?.url;
            if (!url) throw new Error("Download link unavailable");
            window.open(url, "_blank", "noopener");
        } catch (err) {
            console.error("Failed to download attachment:", err);
            setError(err.response?.data?.error || "Unable to download attachment. Please try again.");
        } finally {
            setDownloadingKey(null);
        }
    };

    const handleRemoveAttach = (key) => {
        setState((prev) => ({
            ...prev,
            fields: { ...prev.fields, [key]: { ...prev.fields[key], attachment: null } },
        }));
    };

    const hasEnteredValue = (fieldType, value) => {
        if (value == null) return false;
        if (fieldType === "number") {
            if (value === "" || value === 0) return false;
            if (typeof value === "number") return Number.isFinite(value) && value !== 0;
            const parsed = parseFloat(value);
            return Number.isFinite(parsed) && parsed !== 0;
        }
        return String(value).trim().length > 0;
    };

    // Build cleaned metadata + files map, then await parent onSave
    const handleSave = async () => {
        setSaving(true);
        try {
            for (const f of fieldDefs) {
                const cur = state.fields[f.key] || { value: "", evidenceDate: null, attachment: null };
                const valueEntered = hasEnteredValue(f.type, cur.value);

                if (valueEntered && !cur.evidenceDate) {
                    setError(`Please select an Evidence Date for "${f.label}".`);
                    return;
                }
            }

            const cleanedMeta = {};
            const files = {}; // key -> File

            fieldDefs.forEach((f) => {
                const cur = state.fields[f.key] || { value: "", evidenceDate: null, attachment: null };
                let value = cur.value;
                if (f.type === "number") {
                    // Keep empty values as "" so they don't show as "0" on edit
                    if (value === "" || value == null) {
                        value = "";
                    } else {
                        const n = typeof value === "number" ? value : parseFloat(value);
                        value = isFinite(n) ? n : "";
                    }
                }
                const base = {
                    value,
                    evidenceDate: cur.evidenceDate ? new Date(cur.evidenceDate).toISOString() : null,
                };

                if (cur.attachment && cur.attachment._file) {
                    // New file selected — omit attachment in JSON, send File separately
                    cleanedMeta[f.key] = base;
                    files[f.key] = cur.attachment._file;
                } else {
                    // Keep existing attachment object (with url) or null to remove
                    cleanedMeta[f.key] = { ...base, attachment: cur.attachment ?? null };
                }
            });

            await onSave?.({
                id: initialPurchase?._id || null,
                dateOfPurchase: state.dateOfPurchase || "",
                metadata: cleanedMeta,
                files,
                mode,
            });
        } catch (e) {
            setError(e?.message || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog
                open={open}
                onClose={busy || saving ? undefined : onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { mx: 1 } }}
            >
                <DialogTitle>Conference Details</DialogTitle>

                <DialogContent dividers sx={{ pt: 1 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {/* header row for alignment */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 220px 520px" },
                            columnGap: 2,
                            rowGap: 1,
                            mb: 1,
                        }}
                    >
                    </Box>

                    {fieldDefs.map((f) => {
                        const fv = state.fields[f.key] || { value: "", evidenceDate: null, attachment: null };

                        // derive a presentable name for existing url-only attachments
                        const existingName =
                            fv.attachment?.name ||
                            (fv.attachment?.url ? fileNameFromUrl(String(fv.attachment.url)) : "");

                        const hasExistingAttachment = fv.attachment && !fv.attachment?._file;
                        const hasBlobKey = !!fv.attachment?.blobKey;
                        const downloadDisabled = busy || saving || downloadingKey === f.key;

                        return (
                            <Box
                                key={f.key}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 200px 280px" },
                                    columnGap: 2,
                                    rowGap: 1,
                                    alignItems: "start",
                                    mb: 1,
                                }}
                            >
                                {/* VALUE */}
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                                    <FieldInput
                                        type={f.type}
                                        value={fv.value}
                                        placeholder={getPlaceholder(f.key, f.label)}
                                        onChange={(v) => handleChangeValue(f.key, v)}
                                    />
                                </Box>

                                {/* DATE */}
                                <Box sx={{ width: { md: 200 } }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: { xs: "block", md: "block" }, visibility: { xs: "visible", md: "hidden" } }}
                                    >
                                        Evidence Date
                                    </Typography>
                                    <DatePicker
                                        value={fv.evidenceDate || null}
                                        onChange={(d) => handleChangeDate(f.key, d)}
                                        disabled={busy || saving}
                                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                                    />
                                </Box>

                                {/* ATTACHMENT */}
                                <Box sx={{ width: { md: 520 } }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: { xs: "block", md: "block" }, visibility: { xs: "visible", md: "hidden" } }}
                                    >
                                        Attachment
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            flexWrap: "nowrap",          // keep on one row on desktop
                                            overflow: "hidden",          // trim long names gracefully
                                        }}
                                    >
                                        {/* Existing attachment chip (name) */}
                                        {hasExistingAttachment && (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={truncate(existingName || "Existing file", 26)}
                                                title={existingName}
                                                sx={{
                                                    maxWidth: 220,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    minWidth: 0,
                                                    flexShrink: 1,
                                                }}
                                            />
                                        )}

                                        {/* Download button for existing attachment */}
                                        {hasExistingAttachment && hasBlobKey && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={{ flexShrink: 0 }}
                                                onClick={() => handleDownloadAttachment(f.key)}
                                                disabled={downloadDisabled}
                                            >
                                                {downloadingKey === f.key ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    "Download"
                                                )}
                                            </Button>
                                        )}

                                        {/* Fallback download link */}
                                        {hasExistingAttachment && !hasBlobKey && fv.attachment?.url && (
                                            <Button
                                                component="a"
                                                href={String(fv.attachment.url)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="outlined"
                                                size="small"
                                                sx={{ flexShrink: 0 }}
                                                download
                                            >
                                                Download
                                            </Button>
                                        )}

                                        {/* Replace or Upload button inline */}
                                        <FileUpload
                                            label={hasExistingAttachment ? "Replace file" : "Upload file"}
                                            multiple={false}
                                            onFilesChange={(arr) => {
                                                const file = arr?.[0] || null;
                                                setState((prev) => ({
                                                    ...prev,
                                                    fields: {
                                                        ...prev.fields,
                                                        [f.key]: {
                                                            ...prev.fields[f.key],
                                                            attachment: file
                                                                ? {
                                                                    url: prev.fields[f.key]?.attachment?.url || null, // keep old URL until save
                                                                    name: file.name,
                                                                    size: file.size,
                                                                    type: file.type,
                                                                    _file: file,
                                                                }
                                                                : null,
                                                        },
                                                    },
                                                }));
                                            }}
                                            buttonSx={{
                                                // keep the button compact and inline
                                                display: "inline-flex",
                                                whiteSpace: "nowrap",
                                                minWidth: 140,
                                                px: 2,
                                            }}
                                        />

                                        {/* Remove (trash) */}
                                        {fv.attachment && (
                                            <Tooltip title="Remove file">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRemoveAttach(f.key)}
                                                    disabled={busy || saving}
                                                    sx={{ flexShrink: 0 }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary">
                        Tip: If you enter a value for any line item, an Evidence Date is required for that line item.
                    </Typography>
                </DialogContent>

                <DialogActions>
                    {mode === "edit" && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => onDelete?.(initialPurchase?._id)}
                            disabled={busy || saving}
                            type="button"
                        >
                            Delete
                        </Button>
                    )}
                    <Button variant="outlined" onClick={onClose} disabled={busy || saving} type="button">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={busy || saving}
                        type="button"
                        startIcon={saving ? <CircularProgress size={16} /> : null}
                    >
                        {mode === "edit" ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
}

function getPlaceholder(key, label) {
    const placeholders = {
        name: "Enter conference name",
        registration: "Enter registration fee",
        airfare: "Enter airfare fee",
        carRental: "Enter car rental fee",
        taxi: "Enter taxi/rideshare fee",
        gas: "Enter gas fee",
        hotel: "Enter hotel/lodging fee",
        parking: "Enter parking fee",
        tolls: "Enter tolls fee",
        meals: "Enter meals fee",
    };
    return placeholders[key] || `Enter ${label.toLowerCase()}`;
}

function FieldInput({ type, value, onChange, placeholder }) {
    if (type === "number") {
        return (
            <TextField
                fullWidth
                size="small"
                type="number"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
                placeholder={placeholder}
                inputProps={{ min: 0, step: "0.01" }}
            />
        );
    }
    return (
        <TextField
            fullWidth
            size="small"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
        />
    );
}

function truncate(str, max = 26) {
    if (!str) return "";
    if (str.length <= max) return str;
    const dot = "...";
    return str.slice(0, max - dot.length) + dot;
}

function fileNameFromUrl(url) {
    try {
        const u = new URL(url);
        const last = u.pathname.split("/").pop() || "";
        return decodeURIComponent(last) || url;
    } catch {
        // fallback if not a valid URL
        const parts = url.split("/");
        return parts[parts.length - 1] || url;
    }
}